import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { breakpoints } from '@etg/wings';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import {
  BoundNew,
  ID,
  SellSpecificationNew,
  TravelerNew,
  TravelerPrice,
  TripCharacteristics,
} from '@eti/types';
import { constants } from '@eti/utils';
import isEmpty from 'lodash/isEmpty';
import { VALID_WITH_VOUCHER } from '../../../../constants/tripCharacteristics';
import { useTravelerPricesToDataLayerEffect } from '../../../../common/data-layer/dataLayer';
import { getTravelerPrices } from '../../../../utils/travelers';
import { addLegendNumberToSegmentEvents } from '../../../../widgets/trip-details/utils/format';
import {
  getCFARTicketConfig,
  getFlexibleTicketConfig,
  isCFARTicketAllowedForCarriers,
  isFlexibleTicketAllowedForCarriers,
  shouldShowPartnerLogo,
} from '../../utils/resultUtils';
import TripDetailsBoundSkeleton from './skeleton/TripDetailsBoundSkeleton';
import TripTicketTypeSkeleton from './skeleton/TripTicketTypeSkeleton';
import ResultTrip from './ResultTrip';

const { SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS } = constants;

const buttonWrapperSkeletonStyles = css`
  background-color: #fcfcfc;
  border-bottom-left-radius: 0.1875rem;
  border-bottom-right-radius: 0.1875rem;
  border-top: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (min-width: ${breakpoints._768}) {
    flex-direction: row;
  }
`;

const SelectedTrip = ({
  addTrip,
  availableExtraProducts,
  bounds,
  flightId,
  hasBookingButton,
  includedCheckedBaggage,
  isMultiStop,
  isSearchLoading,
  isSelectedTripRefetching,
  isSelectTripLoading,
  isVI,
  numberOfTravelers,
  onShowAncillaryBundleModal,
  paymentMethodPrices,
  redirectToDBF,
  redirectToTravelerDetails,
  selectionKey,
  shareableUrl,
  shouldDisplayBrandedFares,
  systems,
  travelerPrices,
  travelerPricesWithoutPaymentDiscounts,
  tripCharacteristics,
  tripTravelers,
  voucherAmount,
}) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { language, partner } = useSiteContext();
  const wrapperRef = useRef();

  useTravelerPricesToDataLayerEffect(travelerPrices, paymentMethodPrices);

  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.setAttribute('data-shareable-url', shareableUrl);
    }
  }, [wrapperRef, shareableUrl]);

  const flexibleTicketConfig = getFlexibleTicketConfig(availableExtraProducts);
  const cfarTicketConfig = getCFARTicketConfig(availableExtraProducts);

  const flexibleTicketAvailability =
    isFlexibleTicketAllowedForCarriers(bounds, p('Result.HideFlexTicket.Carriers')) &&
    p('IbeClient.SearchResult.Flex.Behaviour') ===
      SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS.AIR_REBOOKING &&
    !flexibleTicketConfig?.selectedWithTrip
      ? flexibleTicketConfig
      : null;

  const cfarTicketAvailability =
    isCFARTicketAllowedForCarriers(bounds, p('Result.HideCFAR.Carriers')) &&
    p('IbeClient.SearchResult.Flex.Behaviour') ===
      SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS.CANCEL_FOR_ANY_REASON &&
    !cfarTicketConfig?.selectedWithTrip
      ? cfarTicketConfig
      : null;

  return (
    <div ref={wrapperRef}>
      {!isSearchLoading ? (
        <ResultTrip
          addTrip={addTrip}
          bounds={addLegendNumberToSegmentEvents(bounds)}
          cfarTicketAvailability={cfarTicketAvailability}
          discountAmount={voucherAmount?.value ?? 0}
          flexibleTicketAvailability={flexibleTicketAvailability}
          flightId={flightId}
          hasBookingButton={hasBookingButton}
          isCFARTicketIncluded={cfarTicketConfig?.selectedWithTrip}
          isCheckedBaggageIncluded={!isEmpty(includedCheckedBaggage)}
          isMultiStop={isMultiStop}
          isSelectTripLoading={isSelectTripLoading}
          isSelectedTripRefetching={isSelectedTripRefetching}
          isVI={isVI}
          language={language.code}
          numberOfTravelers={numberOfTravelers}
          onShowAncillaryBundleModal={onShowAncillaryBundleModal}
          paymentMethodPrices={paymentMethodPrices}
          redirectToDBF={redirectToDBF}
          redirectToTravelerDetails={redirectToTravelerDetails}
          selectionKey={selectionKey}
          shouldDisplayBrandedFares={shouldDisplayBrandedFares}
          shouldShowPartnerLogo={shouldShowPartnerLogo(
            partner,
            p('Result.SelectedTrip.MetaLogoVisible.List'),
          )}
          title={t('Air.Result.SelectedTrip.Header')}
          travelerPriceBreakdown={getTravelerPrices(
            tripTravelers,
            travelerPricesWithoutPaymentDiscounts,
          )}
          travelerPrices={getTravelerPrices(tripTravelers, travelerPrices)}
          tripCharacteristics={tripCharacteristics}
          tripProviderSystems={systems}
          validWithVoucher={
            tripCharacteristics?.includes(VALID_WITH_VOUCHER) &&
            p('Result.SelfServiceRebooking.ValidWithVoucherTag.Enable')
          }
        />
      ) : (
        <>
          <TripDetailsBoundSkeleton />
          <TripDetailsBoundSkeleton />
          <div className={buttonWrapperSkeletonStyles}>
            <TripTicketTypeSkeleton />
            <TripTicketTypeSkeleton />
          </div>
        </>
      )}
    </div>
  );
};

SelectedTrip.propTypes = {
  addTrip: PropTypes.func,
  availableExtraProducts: PropTypes.arrayOf(
    PropTypes.shape({
      productId: ID,
      sellSpecification: SellSpecificationNew,
    }),
  ),
  bounds: PropTypes.arrayOf(BoundNew),
  flightId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasBookingButton: PropTypes.bool,
  includedCheckedBaggage: PropTypes.shape({
    pieces: PropTypes.number,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  isMultiStop: PropTypes.bool,
  isSearchLoading: PropTypes.bool,
  isSelectedTripRefetching: PropTypes.bool,
  isSelectTripLoading: PropTypes.bool,
  isVI: PropTypes.bool,
  numberOfTravelers: PropTypes.number,
  onShowAncillaryBundleModal: PropTypes.func,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.shape({
        value: PropTypes.number,
      }),
    }),
  ),
  redirectToDBF: PropTypes.func,
  redirectToTravelerDetails: PropTypes.func,
  selectionKey: PropTypes.string,
  shareableUrl: PropTypes.string,
  shouldDisplayBrandedFares: PropTypes.bool,
  systems: PropTypes.arrayOf(PropTypes.string),
  travelerPrices: PropTypes.arrayOf(TravelerPrice),
  travelerPricesWithoutPaymentDiscounts: PropTypes.arrayOf(TravelerPrice),
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristics),
  tripTravelers: PropTypes.arrayOf(TravelerNew),
  voucherAmount: PropTypes.shape({
    value: PropTypes.number,
  }),
};

export default SelectedTrip;
